import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import clsx from 'clsx'
import { ColumnInstance, Row, useTable } from 'react-table'
import { KTCardBody } from '../../../../../../_metronic/helpers'
import { ContentsListLoading } from '../components/loading/ContentsListLoading'
import { ContentsListPagination } from '../components/pagination/ContentsListPagination'
import { useListView } from '../core/ListViewProvider'
import { useQueryRequest } from '../core/QueryRequestProvider'
import { useQueryResponseData, useQueryResponseLoading } from '../core/QueryResponseProvider'
import { Content } from '../core/_models'
import { CustomHeaderColumn } from '../table/columns/CustomHeaderColumn'
import { CustomRow } from '../table/columns/CustomRow'
import { contentsColumns } from './columns/_columns'

import '../style.css'

const ContentsTable = () => {
  const contents = useQueryResponseData()
  const isLoading = useQueryResponseLoading()
  const data = useMemo(() => contents, [contents])
  const columns = useMemo(() => contentsColumns, [])
  const { getTableProps, getTableBodyProps, headers, rows, prepareRow } = useTable({
    columns,
    data,
  })

  const { state } = useQueryRequest()
  const { setGalleryIndex, selected, onSelect, setItemIdForUpdate } = useListView();
  const [goNext, setNext] = useState(false)
  const [goPrevious, setPrevious] = useState(false)

  // Preview state management
  const [hoveredItem, setHoveredItem] = useState<number | null>(null)
  const [previewPosition, setPreviewPosition] = useState({ top: 0, left: 0 })
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const previewRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isOverCheckbox, setIsOverCheckbox] = useState(false)

  // Preload higher quality images
  useEffect(() => {
    if (contents) {
      contents.forEach(item => {
        if (item.avatar) {
          const img = new Image()
          img.src = item.avatar + "&auto=format&fit=crop&w=1200&q=90"
        }
      })
    }
  }, [contents])

  const handleMouseEnter = (index: number, event: React.MouseEvent<HTMLDivElement>) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }

    if (event.target instanceof Element) {
      if (event.target.closest('.template-item-checkbox')) {
        setIsOverCheckbox(true)
        return
      }
    }
    setIsOverCheckbox(false)

    // Calculate optimal preview position
    const rect = event.currentTarget.getBoundingClientRect()
    const windowWidth = window.innerWidth
    const windowHeight = window.innerHeight
    const previewWidth = Math.min(windowWidth * 0.8, 600)
    const previewHeight = Math.min(windowHeight * 0.8, 400)

    let left = rect.left + rect.width / 2
    let top = rect.top + rect.height / 2

    // Adjust position to prevent clipping
    if (left + previewWidth / 2 > windowWidth) {
      left = windowWidth - previewWidth / 2 - 20
    } else if (left - previewWidth / 2 < 0) {
      left = previewWidth / 2 + 20
    }

    if (top + previewHeight / 2 > windowHeight) {
      top = windowHeight - previewHeight / 2 - 20
    } else if (top - previewHeight / 2 < 0) {
      top = previewHeight / 2 + 20
    }

    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredItem(index)
      setPreviewPosition({ top, left })
    }, 100)
  }

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    setHoveredItem(null)
  }

  const handleCheckboxMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    setHoveredItem(null)
    setIsOverCheckbox(true)
  }, [])

  const handleCheckboxMouseLeave = useCallback(() => {
    setIsOverCheckbox(false)
  }, [])

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  return (
    <KTCardBody className='py-4'>
      <div className='table-responsive' style={{ maxHeight: '60vh', overflowY: 'auto' }} ref={containerRef}>
        {
          state.view !== 'gallery' ?
            <table
              id='kt_table_contents'
              className='table fs-6 gy-5 dataTable table-row-bordered table-row-gray-100 align-middle gs-0 '
              {...getTableProps()}
            >
              <thead>
                <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                  {headers?.map((column: ColumnInstance<Content>) => (
                    <CustomHeaderColumn key={column.id} column={column} />
                  ))}
                </tr>
              </thead>
              <tbody
                className='text-gray-600 fw-bold' {...getTableBodyProps()}
              >
                {rows.length > 0 ? (
                  rows?.map((row: Row<Content>, i) => {
                    prepareRow(row)
                    return <CustomRow row={row} key={`row-${i}-${row.id}`} />
                  })
                ) : (
                  <tr>
                    <td colSpan={7}>
                      <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                        No matching records found
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            :
            <div 
              style={{ 
                marginTop: 10, 
                marginBottom: 40, 
                paddingLeft: "1%", 
                display: 'flex', 
                flexWrap: 'wrap', 
                justifyContent: 'center',
                position: 'relative'
              }}
            >
              {
                contents?.map((item, index) =>
                  <div 
                    key={item.id} 
                    style={{ 
                      position: 'relative', 
                      width: '17%', 
                      margin: '1%',
                      transition: 'transform 0.2s ease-out',
                      transform: hoveredItem === index ? 'scale(1.02)' : 'scale(1)',
                    }}
                    onMouseEnter={(e) => handleMouseEnter(index, e)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div style={{
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      transition: 'box-shadow 0.2s ease-out',
                    }}>
                      <a
                        data-src={item.original}
                        draggable={false}
                        onClick={() => {
                          if (selected.length) {
                            onSelect(item.id)
                          }
                          else {
                            setItemIdForUpdate(item.id)
                          }
                        }}
                        style={{
                          display: 'block',
                          position: 'relative',
                          cursor: 'pointer'
                        }}
                      >
                        {item.avatar ? (
                          <img
                            className={`img-thumbnail shadow-none ${selected.includes(item.id) ? 'selected' : ''}`}
                            style={{
                              width: "100%",
                              height: "180px",
                              objectFit: 'cover',
                              transition: 'opacity 0.2s ease-out'
                            }}
                            src={item.avatar + "&auto=format&fit=crop&w=240&q=80"}
                            alt={item.name || 'Content'}
                          />
                        ) : item.video ? (
                          <div className='symbol-label'>
                            <video
                              className={`img-thumbnail shadow-none ${selected.includes(item.id) ? 'selected' : ''}`}
                              src={item.video}
                              controls={false}
                              muted
                              playsInline
                              style={{
                                width: "100%",
                                height: "180px",
                                objectFit: 'cover',
                                transition: 'opacity 0.2s ease-out'
                              }}
                            />
                          </div>
                        ) : null}
                      </a>
                    </div>

                    <div
                      className='template-item-checkbox form-check form-check-sm form-check-custom form-check-solid'
                      style={{ 
                        position: 'absolute', 
                        top: '10px', 
                        left: '10px', 
                        zIndex: 1001
                      }}
                      onMouseEnter={handleCheckboxMouseEnter}
                      onMouseLeave={handleCheckboxMouseLeave}
                    >
                      <input
                        className='form-check-input'
                        type='checkbox'
                        data-kt-check={selected.includes(item.id)}
                        data-kt-check-target='#kt_table_contents .form-check-input'
                        checked={selected.includes(item.id)}
                        onChange={() => onSelect(item.id)}
                      />
                    </div>
                  </div>
                )
              }
              
              {/* Quick Look Preview */}
              {hoveredItem !== null && contents[hoveredItem] && (
                <div
                  ref={previewRef}
                  style={{
                    position: 'fixed',
                    top: previewPosition.top,
                    left: previewPosition.left,
                    transform: 'translate(-50%, -50%)',
                    width: 'auto',
                    maxWidth: '80vw',
                    maxHeight: '80vh',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
                    zIndex: 1000,
                    background: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    pointerEvents: 'none',
                    animation: 'elegantPopIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
                    transformStyle: 'preserve-3d',
                  }}
                >
                  {contents[hoveredItem].avatar ? (
                    <img
                      src={contents[hoveredItem].avatar + "&auto=format&fit=crop&w=1200&q=90"}
                      style={{
                        maxWidth: '100%',
                        maxHeight: 'calc(80vh - 60px)',
                        objectFit: 'contain',
                        display: 'block',
                        transition: 'opacity 0.3s ease-out'
                      }}
                      alt={contents[hoveredItem].name || 'Preview'}
                    />
                  ) : contents[hoveredItem].video ? (
                    <video
                      src={contents[hoveredItem].video}
                      autoPlay
                      loop
                      muted
                      playsInline
                      style={{
                        maxWidth: '100%',
                        maxHeight: 'calc(80vh - 60px)',
                        objectFit: 'contain',
                        display: 'block',
                        transition: 'opacity 0.3s ease-out'
                      }}
                    />
                  ) : null}
                  <div style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'rgba(255,255,255,0.9)',
                    borderTop: '1px solid rgba(0,0,0,0.1)',
                    fontSize: '14px',
                    color: '#1a1a1a',
                    textAlign: 'center',
                    fontWeight: 500,
                    transition: 'opacity 0.3s ease-out'
                  }}>
                    {contents[hoveredItem].name || 'Untitled'}
                  </div>
                </div>
              )}
            </div>
        }
      </div>
      <ContentsListPagination goNext={goNext} goPrevious={goPrevious} />
      {isLoading && <ContentsListLoading />}

      {/* CSS animations */}
      <style>
        {`
          @keyframes elegantPopIn {
            0% { 
              opacity: 0; 
              transform: translate(-50%, -50%) scale(0.9);
              filter: blur(2px);
            }
            100% { 
              opacity: 1; 
              transform: translate(-50%, -50%) scale(1);
              filter: blur(0);
            }
          }
          
          .img-thumbnail:hover {
            opacity: 0.95;
          }

          .img-thumbnail.selected {
            outline: 2px solid #009ef7;
            outline-offset: -2px;
          }
        `}
      </style>
    </KTCardBody>
  )
}

export { ContentsTable }